import { type KeyboardEvent, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { cssVar } from '@going-once/tokens';
import { Icon, type IconName } from './icons';
import { focusRing } from './primitives';

/**
 * The ••• menu: the design system's `MoreButton` and `OverflowMenu`, as one WAI-ARIA menu
 * button.
 *
 * Items come in groups, split by a rule, with whatever cannot be undone last and in
 * `danger`. **A menu with no items is not drawn at all** — the design system still draws
 * the ••• and opens an empty popover, which offers an action that is not there (spec §8
 * calls it a defect). So a caller with nothing to offer this person passes no items and
 * the cell, or the header, simply has no button.
 */

export interface MenuItem {
  label: string;
  onSelect: () => void;
  icon?: IconName;
  /** Irreversible, or the most consequential thing here: drawn in danger, and put in the last group. */
  tone?: 'danger';
  /** Shown but not offered; the label should say why if it is not obvious. */
  disabled?: boolean;
}

export type MenuGroup = readonly MenuItem[];

/** The groups worth drawing: an empty one is dropped, so no rule separates nothing. */
export function menuGroups(groups: readonly MenuGroup[]): MenuGroup[] {
  return groups.filter((group) => group.length > 0);
}

/**
 * Where the arrow keys move focus inside an open menu, WAI-ARIA's way: down and up wrap,
 * Home and End jump to the ends. Anything else is not the menu's key.
 */
export function menuStep(key: string, index: number, count: number): number | undefined {
  if (count === 0) return undefined;
  switch (key) {
    case 'ArrowDown':
      return (index + 1) % count;
    case 'ArrowUp':
      return (index - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return undefined;
  }
}

/** Which item a key on the closed trigger opens the menu at: Enter, Space and Down the first, Up the last. */
export function openingItem(key: string): 'first' | 'last' | undefined {
  if (key === 'ArrowDown' || key === 'Enter' || key === ' ') return 'first';
  if (key === 'ArrowUp') return 'last';
  return undefined;
}

/** Where the popover sits, in the window's coordinates: above or below the trigger, and how far from the left. */
export interface Placement {
  /** Below the trigger, its top edge here. */
  top?: number;
  /** Above the trigger, its bottom edge this far from the window's bottom. */
  bottom?: number;
  left: number;
  /** Fits neither below nor above: the top is pulled inside the window, and the trigger gap dropped. */
  clamped?: true;
  /** Set only when the menu is taller than the window: its height is capped here and it scrolls. */
  maxHeight?: number;
}

interface Box {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * Where a menu of `menu`'s size opens from a trigger at `trigger`, inside a `view`-sized
 * window. Below the trigger unless it would run off the bottom and fits above; its right
 * edge on the trigger's, as the design system draws it, unless that runs off the left —
 * then its left edge on the trigger's — and never past either side of the window. Where it
 * fits neither below nor above, it is pulled up inside the window (and capped to its height).
 */
export function placeMenu(
  trigger: Box,
  menu: { width: number; height: number },
  view: { width: number; height: number },
): Placement {
  const above = trigger.bottom + menu.height > view.height && trigger.top > menu.height;
  const aligned = trigger.right - menu.width >= 0 ? trigger.right - menu.width : trigger.left;
  const left = Math.max(0, Math.min(aligned, view.width - menu.width));
  if (above) return { bottom: view.height - trigger.top, left };
  if (trigger.bottom + menu.height <= view.height) return { top: trigger.bottom, left };
  // Fits neither way: it stays inside the window, over the trigger if it must, and scrolls
  // when the window is shorter than the menu.
  const height = Math.min(menu.height, view.height);
  const top = Math.max(0, Math.min(trigger.bottom, view.height - height));
  return { top, left, clamped: true, ...(menu.height > view.height ? { maxHeight: view.height } : {}) };
}

export function Menu({ label, groups }: { label: string; groups: readonly MenuGroup[] }) {
  const shown = menuGroups(groups);
  const items = shown.flat();
  const [open, setOpen] = useState<'first' | 'last' | undefined>(undefined);
  const [active, setActive] = useState(0);
  const [placement, setPlacement] = useState<Placement | undefined>(undefined);
  const [triggerFocused, setTriggerFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  const close = (refocus: boolean) => {
    setOpen(undefined);
    setPlacement(undefined);
    if (refocus) trigger.current?.focus();
  };

  const show = (at: 'first' | 'last') => {
    setPlacement(undefined);
    setActive(at === 'first' ? 0 : items.length - 1);
    setOpen(at);
  };

  // The popover renders once unseen, is measured, and is then placed — before the browser
  // paints, so it never shows in the wrong place. Fixed, not absolute: a menu in a table
  // cell must not be clipped by the table's own scrolling box.
  useLayoutEffect(() => {
    if (!open || placement || !popover.current || !trigger.current) return;
    setPlacement(
      placeMenu(trigger.current.getBoundingClientRect(), popover.current.getBoundingClientRect(), {
        width: window.innerWidth,
        height: window.innerHeight,
      }),
    );
  }, [open, placement]);

  // Focus follows the active item while the menu is open and placed (an unseen item takes no focus).
  useEffect(() => {
    if (open && placement) itemRefs.current[active]?.focus();
  }, [open, placement, active]);

  // A press anywhere else, or the page moving under it, closes the menu without taking focus back.
  useEffect(() => {
    if (!open) return;
    const dismiss = () => {
      setOpen(undefined);
      setPlacement(undefined);
    };
    const away = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!popover.current?.contains(target) && !trigger.current?.contains(target)) dismiss();
    };
    document.addEventListener('pointerdown', away);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      document.removeEventListener('pointerdown', away);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [open]);

  if (items.length === 0) return null;

  const choose = (item: MenuItem) => {
    if (item.disabled) return;
    // Focus goes back to the trigger first, so whatever the item opens — a dialog — takes
    // the trigger as the place to return focus to when it closes.
    close(true);
    item.onSelect();
  };

  const onMenuKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      // The menu's Escape, and only the menu's: a dialog the menu sits in stays open.
      event.preventDefault();
      event.stopPropagation();
      close(true);
      return;
    }
    if (event.key === 'Tab') {
      // Focus goes to the trigger and the browser's Tab moves on from there. A dialog around the
      // menu must not hear it: it would find the focus on no stop of its own and send it to its first.
      close(true);
      event.stopPropagation();
      return;
    }
    const next = menuStep(event.key, active, items.length);
    if (next !== undefined) {
      event.preventDefault();
      setActive(next);
    }
  };

  let index = 0;
  return (
    <span style={{ display: 'inline-flex' }}>
      <button
        ref={trigger}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open !== undefined}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close(true) : show('first'))}
        onKeyDown={(event) => {
          // Enter and Space arrive as a click; the arrows are the menu button's own.
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
          event.preventDefault();
          show(openingItem(event.key) ?? 'first');
        }}
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
        onFocus={(event) => setTriggerFocused(event.currentTarget.matches(':focus-visible'))}
        onBlur={() => setTriggerFocused(false)}
        style={{
          display: 'inline-grid',
          placeItems: 'center',
          flexShrink: 0,
          width: cssVar('size.target.min'),
          height: cssVar('size.target.min'),
          padding: 0,
          border: 'none',
          background: 'transparent',
          color: cssVar('color.fg.default'),
          cursor: 'pointer',
          // The ring is drawn on the visible circle inside; the browser's own would be a second one.
          outline: 'none',
        }}
      >
        <span
          style={{
            display: 'inline-grid',
            placeItems: 'center',
            boxSizing: 'border-box',
            width: cssVar('size.icon.button'),
            height: cssVar('size.icon.button'),
            borderRadius: cssVar('radius.full'),
            border: `1px solid ${cssVar('color.border.strong')}`,
            background: hovered || open ? cssVar('color.neutral.tint') : 'transparent',
            ...(triggerFocused ? focusRing : {}),
          }}
        >
          <Icon name="ellipsis" />
        </span>
      </button>
      {open ? (
        <div
          ref={popover}
          id={menuId}
          role="menu"
          aria-label={label}
          tabIndex={-1}
          onKeyDown={onMenuKey}
          style={{
            position: 'fixed',
            ...(placement === undefined ? { top: 0, left: 0, visibility: 'hidden' } : { left: placement.left }),
            ...(placement?.top !== undefined
              ? { top: placement.top, ...(placement.clamped ? {} : { marginTop: cssVar('space.xs') }) }
              : {}),
            ...(placement?.bottom !== undefined ? { bottom: placement.bottom, marginBottom: cssVar('space.xs') } : {}),
            ...(placement?.maxHeight !== undefined ? { maxHeight: placement.maxHeight, overflowY: 'auto' } : {}),
            zIndex: 40,
            boxSizing: 'border-box',
            minWidth: cssVar('size.menu.min'),
            maxWidth: cssVar('size.menu.max'),
            padding: cssVar('space.xs'),
            background: cssVar('color.bg.sheet'),
            border: `1px solid ${cssVar('color.border.strong')}`,
            borderRadius: cssVar('radius.md'),
            boxShadow: cssVar('elevation.overlay'),
            textAlign: 'left',
          }}
        >
          {shown.map((group, groupIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: groups have no identity but their order.
            <div key={groupIndex}>
              {groupIndex > 0 ? (
                <hr
                  style={{
                    margin: `${cssVar('space.xs')} 0`,
                    border: 'none',
                    borderTop: `1px solid ${cssVar('color.border.default')}`,
                  }}
                />
              ) : null}
              {/* biome-ignore lint/a11y/useSemanticElements: a menu's group is `role="group"` (the WAI-ARIA menu pattern); a fieldset groups form controls. */}
              <div role="group" style={{ display: 'flex', flexDirection: 'column' }}>
                {group.map((item) => {
                  const at = index++;
                  return (
                    <Item
                      key={item.label}
                      item={item}
                      active={at === active}
                      itemRef={(element) => {
                        itemRefs.current[at] = element;
                      }}
                      onHover={() => setActive(at)}
                      onChoose={() => choose(item)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </span>
  );
}

function Item({
  item,
  active,
  itemRef,
  onHover,
  onChoose,
}: {
  item: MenuItem;
  active: boolean;
  itemRef: (element: HTMLButtonElement | null) => void;
  onHover: () => void;
  onChoose: () => void;
}) {
  const [focused, setFocused] = useState(false);
  const danger = item.tone === 'danger';
  return (
    <button
      ref={itemRef}
      type="button"
      role="menuitem"
      // Roving: the menu moves focus itself, so no item is a tab stop.
      tabIndex={-1}
      aria-disabled={item.disabled ? true : undefined}
      onClick={onChoose}
      onPointerEnter={onHover}
      onFocus={(event) => setFocused(event.currentTarget.matches(':focus-visible'))}
      onBlur={() => setFocused(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: cssVar('space.md'),
        width: '100%',
        boxSizing: 'border-box',
        minHeight: cssVar('size.target.min'),
        padding: `0 ${cssVar('space.md')}`,
        border: 'none',
        borderRadius: cssVar('radius.sm'),
        // A danger row's highlight is its own tint: `neutral.tint` under the danger red is 4.20:1 in dark.
        background:
          active && !item.disabled ? cssVar(danger ? 'color.danger.subtle' : 'color.neutral.tint') : 'transparent',
        color: cssVar(danger ? 'color.danger' : 'color.fg.default'),
        opacity: item.disabled ? 0.5 : 1,
        cursor: item.disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
        fontFamily: cssVar('family.base'),
        fontSize: cssVar('type.body.size'),
        fontWeight: cssVar('type.body.weight'),
        lineHeight: cssVar('type.label.line'),
        outline: 'none',
        // The ring inside the row, as the design system draws it: outside, it would collide with the next item.
        ...(focused ? { boxShadow: `inset 0 0 0 ${cssVar('size.focus.ring')} ${cssVar('color.border.focus')}` } : {}),
      }}
    >
      {item.icon ? (
        <span style={{ display: 'inline-flex', color: cssVar(danger ? 'color.danger' : 'color.fg.muted') }}>
          <Icon name={item.icon} />
        </span>
      ) : null}
      <span>{item.label}</span>
    </button>
  );
}
